// libs
import { createContext, useContext } from 'react';
import { AddressDataApi } from 'shared/types/address';

// defs
import {
  ApiItem,
  ApiSizeItem,
  Cart,
  CartItem,
  CouponDiscount,
  HandleRemoveItemProps,
  Reward,
  RemoveCartItemProps,
  CartShippingData,
  FomoTagDetail,
  FreeGiftsBanner,
  DeltaCommunicationDTO,
} from 'shared/types/cart';

interface CartContext {
  isFetching: boolean;
  isTaggedCouponReady: boolean;
  bestCouponEnabled: boolean;
  freeItems: Cart['freeItems'];
  products: Cart['items'];
  unselectedItems: Cart['items'];
  outOfStockItems: CartItem[];
  outOfStockItemsForCheckout: CartItem[];
  paymentDetails: Cart['cartTotal'];
  itemCount: number;
  quantity: number;
  currencySymbol: string;
  cart?: Cart;
  grandTotal: number;
  shippingMessage?: string;
  shippingCommunicationText?: string;
  couponCode?: string;
  address?: Cart['address'];
  isAddressContinueEnabled: boolean;
  isCartContinueEnabled: boolean;
  isPaymentSummaryCollapsed: boolean;
  cartErrorMessage?: string;
  cartInfoMessage?: string;
  addressErrorMessage?: string;
  couponDiscount: CouponDiscount;
  removeCartItems: ({ items }: RemoveCartItemProps) => Promise<{ success: boolean }>;
  restoreCartData: (custId: string, source: string) => Promise<{ success: boolean }>;
  updateCartItems: (props: { items: ApiItem[] }) => Promise<{ success: boolean; message?: string }>;
  replaceCartItemSize: (props: {
    items: ApiSizeItem[];
  }) => Promise<{ success: boolean; message?: string }>;
  addItemsToCart: (productIds: string[]) => Promise<{ success: boolean; message?: string }>;
  setShippingAddress: (addressId: string) => Promise<{ success: boolean; message?: string }>;
  setGuestShippingAddress: (
    address: Omit<AddressDataApi, 'addressId'>
  ) => Promise<{ success: boolean; message?: string }>;
  handleRemoveItems: ({
    items,
    onSuccess,
    onMessage,
    onSuccessToastProps,
    moveItemToHistory,
  }: HandleRemoveItemProps) => Promise<{ success: boolean }>;

  handleCartRefresh: () => Promise<{ success: boolean }>;

  // coupons v2
  applyCoupon: (
    couponCode: string
  ) => Promise<{ success: boolean; message?: string; discount?: number }>;
  removeAndApplyCoupon: (
    couponCode: string
  ) => Promise<{ success: boolean; message?: string; discount?: number }>;
  removeCoupon: (interactionLocation?: string) => Promise<{ success: boolean; message?: string }>;
  showCouponAppliedNudge: boolean;
  setShowCouponAppliedNudge: (state: boolean) => void;
  offerNudgeTrigger: number;
  openOfferNudge: VoidFunction;
  setPaymentSummaryCollapsed: (state: boolean) => void;
  appliedOfferLabelDetails?: Cart['appliedOfferLabelDetails'];
  rewardsDTO: Reward;
  toggleRewards: () => Promise<{ success: boolean }>;
  setShowRemoveConfirmOOS: (state: boolean) => void;
  showRemoveConfirmOOS: boolean;
  convenienceChargesData: CartShippingData['convenienceChargesDTO'];
  fomoTagDTO: FomoTagDetail;
  freeGiftsBannerData: FreeGiftsBanner;
  freeSamples: Cart['freeSamples'];
  removeOOSFreeSamples: boolean;
  setRemoveOOSFreeSamples: (state: boolean) => void;
  OOSFreeItems: CartItem[];
  overQuantityItems: CartItem[];
  showOverQuantitySheet: boolean;
  setShowOverQuantitySheet: (state: boolean) => void;
  setShippingAddressV2: (
    addressId: string,
    source: string
  ) => Promise<{ success: boolean; message?: string }>;
  refreshCart: () => Promise<{
    success: boolean;
    cart?: Cart;
    message?: string;
    pincodeCity?: string;
  } | void>;
  setShowRemoveConfirmOOSProductId: (productId: string) => void;
  showRemoveConfirmProductId: string;
  showRemovePartialItemCart: boolean;
  setShowRemovePartialItem: (state: boolean) => void;
  partialOOSItems: CartItem[];
  setShowFreeGiftNudge: (state: boolean) => void;
  showFreeGiftNudge: boolean;
  setShowRestoreCart: (state: boolean) => void;
  showRestoreCart: boolean;
  setDeltaDTOData: (state: boolean) => void;
  deltaDTOData: DeltaCommunicationDTO;
  setSizeReplaceToastValue: (state: string) => void;
  sizeReplaceToastValue: string;
  sizeReplaceToastType: string;
  setSizeReplaceToastType: (state: string) => void;
  handleRemoveCartItemsToHistory: ({ items }: RemoveCartItemProps) => Promise<{ success: boolean }>;
  addHistoryItemsToCart: (productIds: string[]) => Promise<{ success: boolean; message?: string }>;
  addItemsToCartRefresh: (
    productIds: CartItem[],
    moveHistoryToItem?: boolean
  ) => Promise<{ success: boolean; message?: string }>;
  cartItemRanks: { productId: string; rank: number }[];
  setCartItemRanks: (items: { productId: string; rank: number }[]) => void;
  showCouponRemoveNudge: boolean;
}

const EMPTY_PROMISE_FUNC = () => Promise.resolve({ success: true });

const freeGiftsBannerData = {
  claimedCount: 0,
  eligibleCount: 0,
  freeSampleMessage: '',
  title: '',
};

const initialValue: CartContext = {
  isFetching: true,
  isTaggedCouponReady: false,
  bestCouponEnabled: false,
  freeItems: [],
  products: [],
  unselectedItems: [],
  outOfStockItems: [],
  outOfStockItemsForCheckout: [],
  currencySymbol: '₹',
  grandTotal: 0,
  itemCount: 0,
  quantity: 0,
  isAddressContinueEnabled: true,
  isCartContinueEnabled: true,
  isPaymentSummaryCollapsed: false,
  removeCartItems: EMPTY_PROMISE_FUNC,
  restoreCartData: EMPTY_PROMISE_FUNC,
  applyCoupon: EMPTY_PROMISE_FUNC,
  updateCartItems: EMPTY_PROMISE_FUNC,
  addItemsToCart: EMPTY_PROMISE_FUNC,
  removeCoupon: EMPTY_PROMISE_FUNC,
  setShippingAddress: EMPTY_PROMISE_FUNC,
  setGuestShippingAddress: EMPTY_PROMISE_FUNC,
  replaceCartItemSize: EMPTY_PROMISE_FUNC,
  handleRemoveItems: EMPTY_PROMISE_FUNC,
  handleCartRefresh: EMPTY_PROMISE_FUNC,
  setPaymentSummaryCollapsed: EMPTY_PROMISE_FUNC,
  rewardsDTO: {},
  toggleRewards: EMPTY_PROMISE_FUNC,
  setShowRemoveConfirmOOS: () => {},
  showRemoveConfirmOOS: false,
  setShowFreeGiftNudge: () => {},
  showFreeGiftNudge: false,
  convenienceChargesData: [],
  fomoTagDTO: { label: '', endTimeEpoch: 0 },
  freeGiftsBannerData: freeGiftsBannerData,
  freeSamples: [],
  removeOOSFreeSamples: false,
  setRemoveOOSFreeSamples: EMPTY_PROMISE_FUNC,
  OOSFreeItems: [],
  overQuantityItems: [],
  showOverQuantitySheet: false,
  setShowOverQuantitySheet: () => {},
  setShippingAddressV2: EMPTY_PROMISE_FUNC,
  refreshCart: EMPTY_PROMISE_FUNC,
  setShowRemoveConfirmOOSProductId: () => {},
  showRemoveConfirmProductId: '',
  showRemovePartialItemCart: false,
  setShowRemovePartialItem: () => {},
  partialOOSItems: [],
  setShowRestoreCart: () => {},
  showRestoreCart: false,
  deltaDTOData: {
    freeSamplesCommunicationDTO: {
      removedItemDTOList: [],
      totalItemsRemoved: 0,
      freeSampleCommunicationMessage: '',
      highlightedText: '',
    },
    priceDeltaText: null,
    freeProductDeltaText: null,
  },
  setDeltaDTOData: () => {},
  setSizeReplaceToastValue: () => {},
  sizeReplaceToastValue: '',
  sizeReplaceToastType: '',
  setSizeReplaceToastType: () => {},
  addHistoryItemsToCart: EMPTY_PROMISE_FUNC,
  handleRemoveCartItemsToHistory: EMPTY_PROMISE_FUNC,
  addItemsToCartRefresh: EMPTY_PROMISE_FUNC,
  cartItemRanks: [],
  setCartItemRanks: EMPTY_PROMISE_FUNC,
  showCouponRemoveNudge: false,
  offerNudgeTrigger: 0,
  openOfferNudge: () => {},
};

const cartContext = createContext(initialValue);

export const useCartContext = () => useContext(cartContext);

export default cartContext;
